import type { SolutionPageExtras, SolutionSubpageSlug } from './types';

export const solutionPageExtras: Record<SolutionSubpageSlug, SolutionPageExtras> = {
  'smart-house': {
    processTitle: 'Partnership process',
    transform: {
      eyebrow: 'TRANSFORMATION',
      title: 'From traditional property to smart community',
      subtitle:
        'Give residents a premium connected experience while giving operators centralized control — without building technology in-house.',
      before: {
        title: 'Before: Traditional property',
        items: [
          'Physical keys and manual access logs',
          'Reactive maintenance after damage occurs',
          'Disconnected devices with no resident app',
          'No portfolio-wide operational visibility',
        ],
      },
      after: {
        title: 'After: Smart residential',
        items: [
          'Mobile keys and visitor passes from your branded app',
          'Proactive leak, climate, and security alerts',
          'Unified lighting, climate, and access controls',
          'Multi-property dashboard for managers',
        ],
      },
      arrowLabel: 'Your brand',
    },
    journey: {
      eyebrow: 'RESIDENT JOURNEY',
      title: 'A seamless smart living experience',
      subtitle:
        'From move-in to daily living — residents interact with your brand at every touchpoint.',
      steps: [
        {
          step: 1,
          icon: 'smartphone',
          title: 'Digital move-in',
          description:
            'Residents download your branded app, receive mobile credentials, and complete unit setup in minutes.',
        },
        {
          step: 2,
          icon: 'home',
          title: 'Daily living',
          description:
            'Control lighting, climate, and scenes from one app. Schedules and automations adapt to resident routines.',
        },
        {
          step: 3,
          icon: 'key',
          title: 'Community access',
          description:
            'Unlock amenities, grant visitor passes, and manage deliveries — all without front-desk friction.',
        },
        {
          step: 4,
          icon: 'shield',
          title: 'Support & safety',
          description:
            'Instant alerts for leaks or security events. Maintenance requests route to staff with full audit trails.',
        },
      ],
    },
  },
  'smart-manufacturing': {
    processTitle: 'Launch process',
    transform: {
      eyebrow: 'TRANSFORMATION',
      title: 'From legacy product to connected device',
      subtitle:
        'Add Wi-Fi or Bluetooth intelligence, a branded app, and fleet analytics — without building an IoT team from scratch.',
      before: {
        title: 'Before: Regular product',
        items: [
          'No connectivity or remote control',
          'No usage data from the field',
          'No OTA firmware update path',
          'High cost to build IoT in-house',
        ],
      },
      after: {
        title: 'After: Smart product',
        items: [
          'Wi-Fi / Bluetooth connected under your brand',
          'Branded mobile app and cloud dashboard',
          'Fleet analytics and health monitoring',
          'OTA updates with staged rollouts',
        ],
      },
      arrowLabel: '3 months',
    },
    journey: {
      eyebrow: 'PRODUCT LIFECYCLE',
      title: 'From prototype to market-ready smart products',
      subtitle:
        'A structured journey that transforms your existing product into a connected, scalable, and production-ready smart solution.',
      steps: [
        {
          step: 1,
          icon: 'cpu',
          title: 'Analyze & prototype',
          description:
            'Assess hardware compatibility, customize firmware, and validate connectivity through rapid prototyping.',
          bullets: ['Compatibility assessment', 'Firmware customization', 'Prototype validation'],
        },
        {
          step: 2,
          icon: 'cloud',
          title: 'Build platform & apps',
          description:
            'Create a branded ecosystem with cloud infrastructure, mobile apps, and device management tools.',
          bullets: ['White-label cloud setup', 'iOS & Android apps', 'Device management portal'],
        },
        {
          step: 3,
          icon: 'factory',
          title: 'Production & QA',
          description:
            'Prepare for mass manufacturing with provisioning, quality assurance, and technical documentation.',
          bullets: ['Factory provisioning', 'Quality assurance', 'Production documentation'],
        },
        {
          step: 4,
          icon: 'chart',
          title: 'Launch & scale',
          description:
            'Go to market with analytics, OTA updates, and ongoing support to scale your product ecosystem.',
          bullets: ['Fleet analytics', 'OTA updates', 'Technical partnership'],
        },
      ],
    },
  },
  'smart-hospitality': {
    processTitle: 'Partnership process',
    ecosystem: {
      eyebrow: 'CONNECTED ECOSYSTEM',
      title: 'One Platform. Every Hospitality Experience Connected.',
      subtitle:
        'Bring together guests, rooms, staff, devices, and property operations into one intelligent hospitality ecosystem.',
      cards: [
        {
          id: 'rooms',
          emoji: '🏨',
          title: 'Smart Rooms',
          illustration: 'room',
          bullets: [
            'Digital room access',
            'Climate automation',
            'Lighting control',
            'Occupancy detection',
          ],
        },
        {
          id: 'guest',
          emoji: '📱',
          title: 'Guest Experience',
          illustration: 'guest',
          bullets: [
            'Mobile check-in',
            'Digital concierge',
            'Room preferences',
            'Service requests',
          ],
        },
        {
          id: 'staff',
          emoji: '🛎',
          title: 'Staff Automation',
          illustration: 'staff',
          bullets: [
            'Housekeeping alerts',
            'Maintenance workflows',
            'Task assignments',
            'Live status updates',
          ],
        },
        {
          id: 'property',
          emoji: '🏢',
          title: 'Property Operations',
          illustration: 'property',
          bullets: [
            'Multi-property dashboard',
            'Energy management',
            'Device monitoring',
            'Access management',
          ],
        },
        {
          id: 'analytics',
          emoji: '📊',
          title: 'Analytics & Insights',
          illustration: 'analytics',
          bullets: [
            'Occupancy reports',
            'Energy analytics',
            'Guest engagement',
            'Operational KPIs',
          ],
        },
      ],
      metrics: [
        { emoji: '⚡', label: '35% Lower Energy Costs' },
        { emoji: '😊', label: '40% Better Guest Satisfaction' },
        { emoji: '🛎', label: '50% Faster Operations' },
        { emoji: '📈', label: '24/7 Property Visibility' },
      ],
    },
    modules: {
      eyebrow: 'SMART HOSPITALITY PLATFORM',
      title: 'Everything Your Hospitality Business Needs. One Connected Platform.',
      subtitle:
        'Digitize guest experiences, automate operations, and manage every property through modular hospitality solutions.',
      items: [
        {
          id: 'access',
          emoji: '🔑',
          title: 'Guest Access',
          illustration: 'access',
          description:
            'Enable digital room keys, mobile check-ins, and secure staff access.',
          tags: ['Mobile Keys', 'QR Access', 'Staff Permissions'],
          metric: '↓ 60% Check-in Time',
        },
        {
          id: 'energy',
          emoji: '⚡',
          title: 'Energy Intelligence',
          illustration: 'energy',
          description: 'Reduce utility costs using occupancy-aware automation.',
          tags: ['HVAC', 'Smart Lighting', 'Analytics'],
          metric: '↓ 35% Energy Costs',
        },
        {
          id: 'rooms',
          emoji: '🏨',
          title: 'Smart Rooms',
          illustration: 'rooms',
          description: 'Personalize every guest stay through connected room controls.',
          tags: ['Climate', 'Scenes', 'Entertainment'],
          metric: '↑ 40% Guest Satisfaction',
        },
        {
          id: 'guest-app',
          emoji: '📱',
          title: 'Guest Experience App',
          illustration: 'guest-app',
          description:
            'A branded app for check-ins, room services, and concierge requests.',
          tags: ['Services', 'Concierge', 'Preferences'],
          metric: '100% Digital Journey',
        },
        {
          id: 'operations',
          emoji: '🛎',
          title: 'Operations Hub',
          illustration: 'operations',
          description: 'Manage housekeeping, maintenance, and staff tasks in real time.',
          tags: ['Tasks', 'Alerts', 'Tracking'],
          metric: '↓ 50% Manual Work',
        },
        {
          id: 'analytics',
          emoji: '📊',
          title: 'Analytics Dashboard',
          illustration: 'analytics',
          description: 'Track occupancy, energy consumption, and guest engagement.',
          tags: ['Reports', 'Insights', 'KPIs'],
          metric: '24/7 Visibility',
        },
      ],
    },
    transform: {
      eyebrow: 'TRANSFORMATION',
      title: 'From standard rooms to smart guest experiences',
      subtitle:
        'Deliver contactless journeys, energy-efficient operations, and branded in-room controls on one platform.',
      before: {
        title: 'Before: Traditional hotel',
        items: [
          'Front-desk key cards and manual check-in',
          'HVAC running in empty rooms',
          'No unified guest mobile experience',
          'Limited operational visibility per room',
        ],
      },
      after: {
        title: 'After: Smart hospitality',
        items: [
          'Mobile check-in and digital room keys',
          'Occupancy-based energy automation',
          'Branded guest app with in-room controls',
          'Real-time housekeeping and maintenance status',
        ],
      },
      arrowLabel: 'Your brand',
    },
    journey: {
      eyebrow: 'GUEST JOURNEY',
      title: 'Contactless hospitality from booking to checkout',
      subtitle:
        'Every guest touchpoint reinforces your brand — before, during, and after their stay.',
      steps: [
        {
          step: 1,
          icon: 'smartphone',
          title: 'Pre-arrival',
          description:
            'Guests complete mobile check-in, select preferences, and receive digital room keys before arrival.',
        },
        {
          step: 2,
          icon: 'key',
          title: 'Arrival & access',
          description:
            'Skip the front desk with contactless entry. Welcome scenes activate lighting and climate automatically.',
        },
        {
          step: 3,
          icon: 'hotel',
          title: 'In-stay experience',
          description:
            'Control rooms, request services, and access amenities from your branded guest application.',
        },
        {
          step: 4,
          icon: 'zap',
          title: 'Checkout & efficiency',
          description:
            'Express checkout triggers room reset, energy savings mode, and housekeeping notifications.',
        },
      ],
    },
    guestJourney: {
      eyebrow: 'CONNECTED GUEST EXPERIENCE',
      title: 'Every Stay Powered by Intelligent Automation',
      subtitle:
        'Deliver a seamless hospitality journey where guests, rooms, staff, and operations stay connected from booking to checkout.',
      hub: {
        title: 'Smart Hospitality Platform',
        description:
          'One intelligent platform connecting guests, rooms, staff, devices, and property operations.',
        capabilities: [
          { emoji: '☁', label: 'Cloud Platform' },
          { emoji: '📶', label: 'IoT Connectivity' },
          { emoji: '📊', label: 'Analytics' },
          { emoji: '🏨', label: 'Property Management' },
        ],
      },
      stages: [
        {
          id: 'booking',
          position: 'top',
          emoji: '📱',
          title: 'Digital Booking',
          bullets: ['Mobile check-in', 'Guest preferences', 'Room assignment'],
          metric: '↓ 60% Check-in Time',
        },
        {
          id: 'arrival',
          position: 'left',
          emoji: '🔑',
          title: 'Smart Arrival',
          bullets: ['Digital room keys', 'Contactless access', 'Personalized welcome'],
          metric: '100% Keyless Entry',
        },
        {
          id: 'insights',
          position: 'right',
          emoji: '📊',
          title: 'Continuous Insights',
          bullets: ['Occupancy analytics', 'Energy reports', 'Operational KPIs'],
          metric: '24/7 Visibility',
        },
        {
          id: 'stay',
          position: 'bottom-left',
          emoji: '🏨',
          title: 'Connected Stay',
          bullets: ['Climate automation', 'Room controls', 'Digital concierge'],
          metric: '↑ 40% Guest Satisfaction',
        },
        {
          id: 'checkout',
          position: 'bottom-right',
          emoji: '🛎',
          title: 'Intelligent Checkout',
          bullets: ['Auto checkout', 'Housekeeping triggers', 'Energy reset'],
          metric: '↓ 50% Manual Tasks',
        },
      ],
    },
    implementationJourney: {
      eyebrow: 'IMPLEMENTATION JOURNEY',
      title: 'From Property Assessment to Smart Hospitality Launch',
      subtitle:
        'A streamlined process that transforms traditional properties into intelligent, connected guest experiences.',
      milestones: [
        {
          id: 'discover',
          emoji: '🔍',
          phase: 'Discover',
          title: 'Property Assessment',
          description:
            "Analyze your property's infrastructure, guest journey, and operational requirements.",
          highlights: ['Property audit', 'Existing system assessment', 'Guest experience mapping'],
        },
        {
          id: 'configure',
          emoji: '⚙️',
          phase: 'Configure',
          title: 'Platform Customization',
          description: 'Configure modules, branding, integrations, and automation rules.',
          highlights: ['White-label setup', 'Staff permissions', 'Workflow automation'],
        },
        {
          id: 'deploy',
          emoji: '🚀',
          phase: 'Deploy',
          title: 'Go Live',
          description: 'Deploy connected devices and launch your smart hospitality platform.',
          highlights: ['Smart devices', 'Mobile app activation', 'Staff onboarding'],
        },
        {
          id: 'optimize',
          emoji: '📈',
          phase: 'Optimize',
          title: 'Continuous Optimization',
          description: 'Use analytics and insights to continuously improve operations.',
          highlights: ['Occupancy analytics', 'Energy reports', 'Performance improvements'],
        },
      ],
    },
    statsRibbon: {
      eyebrow: 'TRUSTED AT SCALE',
      title: 'Built for Reliable Hospitality Operations',
      subtitle:
        'Powering intelligent hospitality experiences with enterprise-grade reliability.',
      items: [
        { emoji: '', value: '30%', label: 'Energy Savings' },
        { emoji: '', value: '50+', label: 'Certified Partners' },
        { emoji: '', value: '24/7', label: 'Dedicated Support' },
        { emoji: '', value: '99.9%', label: 'Platform Uptime' },
        { emoji: '', value: '40%', label: 'Better Guest Satisfaction' },
        { emoji: '', value: 'Enterprise-grade', label: 'Security' },
      ],
    },
  },
  'commercial-lighting': {
    processTitle: 'Partnership process',
    benefitOrbit: {
      eyebrow: 'ADVANTAGES',
      title: 'Smarter Lighting for Commercial Spaces',
      subtitle:
        'Improve efficiency, comfort, safety, and visibility across every commercial environment.',
      hubTitle: 'Smart Lighting Platform',
      hubSubtitle: 'Connected control for every site, zone, and fixture',
      nodes: [
        {
          id: 'energy',
          emoji: '⚡',
          title: 'Deep Energy Savings',
          description:
            'Occupancy and daylight harvesting reduce lighting energy consumption across buildings.',
          position: 'top',
        },
        {
          id: 'multi-site',
          emoji: '🏢',
          title: 'Multi-Site Control',
          description:
            'Manage offices, warehouses, retail stores, and campuses from one dashboard.',
          position: 'left',
        },
        {
          id: 'schedules',
          emoji: '🕐',
          title: 'Automated Schedules',
          description:
            'Create zone-based scenes for working hours, safety, productivity, and ambience.',
          position: 'right',
        },
        {
          id: 'analytics',
          emoji: '📊',
          title: 'Usage Analytics',
          description:
            'Track consumption, faults, performance, and maintenance needs in real time.',
          position: 'bottom',
        },
      ],
      metrics: [
        { value: 'Up to 60%', label: 'Energy Savings' },
        { value: '24/7', label: 'Zone Monitoring' },
        { value: 'Real-Time', label: 'Fault Alerts' },
        { value: 'Multi-Site', label: 'Visibility' },
      ],
    },
    bentoVerticals: {
      eyebrow: 'VERTICALS',
      title: 'Lighting Solutions for Every Commercial Environment',
      subtitle:
        'Deploy intelligent lighting across offices, retail spaces, industrial sites, campuses, and hospitality venues.',
      blocks: [
        {
          id: 'office',
          size: 'large',
          title: 'Office & Corporate',
          tagline:
            'Corporate campuses, coworking spaces, business parks, and headquarters with smart zone control.',
          description:
            'Deliver comfortable, energy-efficient lighting across open offices, meeting rooms, and executive floors — with occupancy-based automation and daylight harvesting.',
          details: [
            'Zone-level dimming for desks, huddle rooms, and common areas',
            'Occupancy sensors that cut waste in unoccupied spaces',
            'Central dashboards for multi-floor and multi-building portfolios',
          ],
          emoji: '🏙️',
          theme: 'blue',
        },
        {
          id: 'industrial',
          size: 'medium',
          title: 'Industrial & Warehouses',
          tagline:
            'High-bay lighting, production floors, warehouses, and distribution centers at scale.',
          description:
            'High-bay and production-floor lighting with automated schedules, fault alerts, and visibility across large-footprint sites.',
          details: [
            'High-bay fixture control with aisle and zone segmentation',
            'Shift-based schedules aligned to production hours',
            'Real-time fault detection for critical safety lighting',
          ],
          emoji: '🏭',
          theme: 'cyan',
        },
        {
          id: 'retail',
          size: 'medium',
          title: 'Retail & Hospitality',
          tagline:
            'Scene-based lighting for stores, restaurants, hotels, and customer-facing spaces.',
          description:
            'Scene-based lighting that elevates customer experience in stores, restaurants, hotels, and branded hospitality spaces.',
          details: [
            'Preset scenes for opening hours, events, and closing routines',
            'Accent and ambient layers tuned per zone or brand guideline',
            'Remote adjustments across franchise or property portfolios',
          ],
          emoji: '🛍️',
          theme: 'violet',
        },
        {
          id: 'education',
          size: 'small',
          title: 'Education & Healthcare',
          tagline:
            'Safe, efficient lighting for schools, hospitals, and institutional facilities.',
          description:
            'Reliable, code-aware lighting for classrooms, corridors, clinics, and patient areas — balancing comfort, safety, and efficiency.',
          details: [
            'Corridor and classroom schedules tied to timetables',
            'Night-mode and emergency-pathway visibility',
            'Usage reporting for sustainability and compliance audits',
          ],
          emoji: '🏥',
          theme: 'green',
        },
        {
          id: 'parking',
          size: 'small',
          title: 'Parking & Outdoor',
          tagline:
            'Automated schedules, security lighting, and outdoor zone control across sites.',
          description:
            'Automated outdoor and parking-lot lighting with motion triggers, security integration, and multi-site schedule control.',
          details: [
            'Motion-activated levels for lots, walkways, and facades',
            'Sunset-to-sunrise schedules with holiday overrides',
            'Unified control for campuses with multiple outdoor zones',
          ],
          emoji: '🅿️',
          theme: 'amber',
        },
      ],
    },
    platformModules: {
      eyebrow: 'PLATFORM MODULES',
      title: 'One Lighting OS for Every Site, Zone, and Fixture',
      subtitle:
        'Control, automate, monitor, and optimize commercial lighting through modular IoT capabilities.',
      modules: [
        { id: 'zones', label: 'Zone Control', position: 'top-left' },
        { id: 'occupancy', label: 'Occupancy Automation', position: 'top' },
        { id: 'daylight', label: 'Daylight Harvesting', position: 'top-right' },
        { id: 'scenes', label: 'Scene Scheduling', position: 'left' },
        { id: 'faults', label: 'Fault Detection', position: 'right' },
        { id: 'energy', label: 'Energy Analytics', position: 'bottom-left' },
        { id: 'access', label: 'Role-Based Access', position: 'bottom' },
        { id: 'multi', label: 'Multi-Site Management', position: 'bottom-right' },
      ],
    },
    transform: {
      eyebrow: 'TRANSFORMATION',
      title: 'From Traditional Lighting to Smart Lighting OS',
      subtitle:
        'Replace manual controls with an intelligent platform built for multi-site commercial operations.',
      before: {
        title: 'Traditional Lighting',
        items: [
          'Manual switches',
          'Fixed schedules',
          'No occupancy visibility',
          'High energy wastage',
          'Reactive maintenance',
        ],
      },
      after: {
        title: 'Smart Lighting OS',
        items: [
          'Automated scenes',
          'Occupancy-aware zones',
          'Energy optimization',
          'Real-time fault detection',
          'Centralized multi-site control',
        ],
      },
      arrowLabel: 'Transform',
    },
    statsRibbon: {
      eyebrow: 'IMPACT',
      title: 'Built for Energy-Efficient Commercial Operations',
      subtitle:
        'Measurable outcomes for facilities teams managing offices, retail, industrial, and campus portfolios.',
      items: [
        { emoji: '⚡', value: 'Up to 60%', label: 'Energy Savings' },
        { emoji: '🏢', value: '128+', label: 'Zones Managed' },
        { emoji: '🔔', value: '24/7', label: 'Fault Monitoring' },
        { emoji: '✅', value: '99.9%', label: 'Platform Uptime' },
        { emoji: '🌐', value: 'Multi-Site', label: 'Control' },
        // { emoji: '🔧', value: 'Lower', label: 'Maintenance Costs' },
      ],
    },
    commercialCta: {
      title: 'Ready to Build Smarter Commercial Spaces?',
      imageSrc: '/images/solutions/smart-commercial-lighting/cta-banner-image-.png',
      imageAlt: 'Smart commercial lighting platform controlling connected buildings and sites',
      primaryCta: { label: 'Partner With Us', to: '/partner-registration' },
      secondaryCta: { label: 'Schedule Demo', to: '/contact-us' },
    },
    journey: {
      eyebrow: 'DEPLOYMENT PATH',
      title: 'How partners roll out intelligent lighting',
      subtitle:
        'From site survey to portfolio-wide analytics — a proven deployment framework.',
      steps: [
        {
          step: 1,
          icon: 'building',
          title: 'Site assessment',
          description:
            'Survey fixtures, drivers, and network readiness across offices, warehouses, or retail locations.',
        },
        {
          step: 2,
          icon: 'lightbulb',
          title: 'Configure zones',
          description:
            'Define dimming zones, occupancy rules, scenes, and schedules tailored to each environment.',
        },
        {
          step: 3,
          icon: 'activity',
          title: 'Automate & optimize',
          description:
            'Enable daylight harvesting, demand response, and safety modes with remote management.',
        },
        {
          step: 4,
          icon: 'chart',
          title: 'Monitor & report',
          description:
            'Track consumption, detect faults, and export sustainability data across your portfolio.',
        },
      ],
    },
  },
};
